import React, { useState, useEffect, useCallback } from "react";
import {
  fetchCoursesAPI,
  createCourseAPI,
  updateCourseAPI,
  deleteCourseAPI,
  fetchPlansByCourseAPI,
  createPlanAPI,
  updatePlanAPI,
  deletePlanAPI,
  firstError,
} from "../../utils/api";
import { showSuccess, showError, showDeleteConfirmation } from "../../utils/sweetAlert";
import { EmptyState, ErrorAlert, LoadingState, Modal, SearchField } from "../../components/ui";
import "./Courses.css";

const EMPTY_PLAN = {
  plan_name: "",
  plan_description: "",
  plan_amount: "",
  plan_discount: "",
  plan_duration: "",
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const formatINR = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "₹0";
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");

  // Course modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseName, setCourseName] = useState("");
  const [courseError, setCourseError] = useState("");
  const [savingCourse, setSavingCourse] = useState(false);

  // Plans
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planFormData, setPlanFormData] = useState(EMPTY_PLAN);
  const [planErrors, setPlanErrors] = useState({});
  const [savingPlan, setSavingPlan] = useState(false);

  const loadCourses = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const response = await fetchCoursesAPI();
      if (response?.success) {
        setCourses(response.data || []);
      } else if (response) {
        setLoadError(response.message || "Couldn't load courses.");
      }
    } catch (error) {
      console.error("Error loading courses:", error);
      setLoadError("Couldn't reach the server to load courses.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  // ===================== Courses =====================

  const openCourseModal = (course = null) => {
    setEditingCourse(course);
    setCourseName(course?.course_name || "");
    setCourseError("");
    setIsModalOpen(true);
  };

  const closeCourseModal = useCallback(() => {
    if (savingCourse) return;
    setIsModalOpen(false);
    setEditingCourse(null);
    setCourseName("");
    setCourseError("");
  }, [savingCourse]);

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    const name = courseName.trim();
    if (!name) {
      setCourseError("Course name is required.");
      return;
    }
    if (name.length > 255) {
      setCourseError("Course name must be less than 255 characters.");
      return;
    }
    if (savingCourse) return;

    setSavingCourse(true);
    try {
      const response = editingCourse
        ? await updateCourseAPI(editingCourse.id, { course_name: name })
        : await createCourseAPI({ course_name: name });

      if (response?.success) {
        // A rename also moves the course's test series and students; the server says how many
        showSuccess(
          editingCourse ? response.message || `"${name}" was updated.` : `"${name}" was created.`,
          editingCourse ? "Course updated" : "Course created"
        );
        setIsModalOpen(false);
        setEditingCourse(null);
        setCourseName("");
        loadCourses();
      } else if (response) {
        setCourseError(firstError(response, "Couldn't save the course."));
      }
    } catch (error) {
      console.error("Error saving course:", error);
      setCourseError("Couldn't reach the server. Please try again.");
    } finally {
      setSavingCourse(false);
    }
  };

  const handleCourseDelete = async (course) => {
    const result = await showDeleteConfirmation("course", course.course_name);
    if (!result.isConfirmed) return;

    try {
      const response = await deleteCourseAPI(course.id);
      if (response?.success) {
        setCourses((prev) => prev.filter((c) => c.id !== course.id));
        showSuccess(`"${course.course_name}" was deleted.`, "Course deleted");
      } else if (response) {
        showError(response.message || "Couldn't delete the course.");
      }
    } catch (error) {
      console.error("Error deleting course:", error);
      showError("Couldn't delete the course. Please try again.");
    }
  };

  // ===================== Plans =====================

  const loadPlans = useCallback(async (courseId) => {
    try {
      setPlansLoading(true);
      const response = await fetchPlansByCourseAPI(courseId);
      if (response?.success) {
        setPlans(response.data || []);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error("Error loading plans:", error);
      showError("Couldn't load plans for this course.");
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const viewCoursePlans = (course) => {
    setSelectedCourse(course);
    setPlans([]);
    loadPlans(course.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closePlansView = () => {
    setSelectedCourse(null);
    setPlans([]);
  };

  const openPlanModal = (plan = null) => {
    setEditingPlan(plan);
    setPlanFormData(
      plan
        ? {
            plan_name: plan.plan_name ?? "",
            plan_description: plan.plan_description ?? "",
            plan_amount: plan.plan_amount ?? "",
            plan_discount: plan.plan_discount ?? "",
            plan_duration: plan.plan_duration ?? "",
          }
        : EMPTY_PLAN
    );
    setPlanErrors({});
    setIsPlanModalOpen(true);
  };

  const closePlanModal = useCallback(() => {
    if (savingPlan) return;
    setIsPlanModalOpen(false);
    setEditingPlan(null);
    setPlanFormData(EMPTY_PLAN);
    setPlanErrors({});
  }, [savingPlan]);

  const handlePlanInputChange = (e) => {
    const { name, value } = e.target;
    setPlanFormData((prev) => ({ ...prev, [name]: value }));
    if (planErrors[name]) setPlanErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validatePlanForm = () => {
    const errors = {};
    const name = String(planFormData.plan_name).trim();
    const amount = String(planFormData.plan_amount).trim();
    const discount = String(planFormData.plan_discount).trim();
    const duration = String(planFormData.plan_duration).trim();

    if (!name) errors.plan_name = "Plan name is required.";
    else if (name.length > 255) errors.plan_name = "Plan name must be less than 255 characters.";

    if (!String(planFormData.plan_description).trim()) errors.plan_description = "Please add a short description.";

    if (!amount) errors.plan_amount = "Price is required.";
    else if (isNaN(amount) || Number(amount) < 0) errors.plan_amount = "Enter a valid price (0 or more).";

    if (discount && (isNaN(discount) || Number(discount) < 0)) {
      errors.plan_discount = "Enter a valid discount (0 or more).";
    } else if (discount && amount && !isNaN(amount) && Number(discount) > Number(amount)) {
      errors.plan_discount = "Discount can't be more than the price.";
    }

    if (!duration) errors.plan_duration = "Duration is required.";
    else if (!Number.isInteger(Number(duration)) || Number(duration) <= 0) {
      errors.plan_duration = "Enter a whole number of months.";
    }

    return errors;
  };

  const handlePlanSubmit = async (e) => {
    e.preventDefault();
    const errors = validatePlanForm();
    if (Object.keys(errors).length > 0) {
      setPlanErrors(errors);
      return;
    }
    if (savingPlan) return;

    const planData = {
      plan_name: String(planFormData.plan_name).trim(),
      plan_description: String(planFormData.plan_description).trim(),
      plan_amount: parseFloat(planFormData.plan_amount),
      plan_discount: String(planFormData.plan_discount).trim() ? parseFloat(planFormData.plan_discount) : null,
      plan_duration: parseInt(planFormData.plan_duration, 10),
    };

    setSavingPlan(true);
    try {
      const response = editingPlan
        ? await updatePlanAPI(editingPlan.id, planData)
        : await createPlanAPI(selectedCourse.id, planData);

      if (response?.success) {
        showSuccess(
          `"${planData.plan_name}" was ${editingPlan ? "updated" : "created"}.`,
          editingPlan ? "Plan updated" : "Plan created"
        );
        setIsPlanModalOpen(false);
        setEditingPlan(null);
        setPlanFormData(EMPTY_PLAN);
        loadPlans(selectedCourse.id);
      } else if (response) {
        // 422: show each message under its field; anything else as a dialog
        const fieldErrors = Object.fromEntries(
          Object.entries(response.errors || {})
            .filter(([field]) => field in EMPTY_PLAN)
            .map(([field, messages]) => [field, [].concat(messages)[0]])
        );
        if (Object.keys(fieldErrors).length) setPlanErrors(fieldErrors);
        else showError(response.message || "Couldn't save the plan.");
      }
    } catch (error) {
      console.error("Error saving plan:", error);
      showError("Couldn't save the plan. Please try again.");
    } finally {
      setSavingPlan(false);
    }
  };

  const handlePlanDelete = async (plan) => {
    const result = await showDeleteConfirmation("plan", plan.plan_name);
    if (!result.isConfirmed) return;

    try {
      const response = await deletePlanAPI(plan.id);
      if (response?.success) {
        setPlans((prev) => prev.filter((p) => p.id !== plan.id));
        showSuccess(`"${plan.plan_name}" was deleted.`, "Plan deleted");
      } else if (response) {
        showError(response.message || "Couldn't delete the plan.");
      }
    } catch (error) {
      console.error("Error deleting plan:", error);
      showError("Couldn't delete the plan. Please try again.");
    }
  };

  const renderFieldError = (key) =>
    planErrors[key] ? (
      <span className="field-error">
        <i className="bi bi-exclamation-circle"></i> {planErrors[key]}
      </span>
    ) : null;

  // ===================== Render =====================

  const planModal = (
    <Modal
      open={isPlanModalOpen}
      onClose={closePlanModal}
      closeOnOverlay={false}
      icon={editingPlan ? "bi bi-pencil-square" : "bi bi-card-list"}
      title={editingPlan ? "Edit plan" : "New plan"}
      subtitle={selectedCourse ? `For ${selectedCourse.course_name}` : undefined}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={closePlanModal} disabled={savingPlan}>
            Cancel
          </button>
          <button type="submit" form="planForm" className="btn-primary" disabled={savingPlan}>
            {savingPlan ? <span className="btn-spinner"></span> : <i className="bi bi-check2"></i>}
            {editingPlan ? "Save changes" : "Create plan"}
          </button>
        </>
      }
    >
      <form id="planForm" onSubmit={handlePlanSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="plan_name">
            Plan name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="plan_name"
            name="plan_name"
            value={planFormData.plan_name}
            onChange={handlePlanInputChange}
            className={planErrors.plan_name ? "error" : ""}
            maxLength="255"
            placeholder="e.g. 6-month full access"
            autoFocus
          />
          {renderFieldError("plan_name")}
        </div>

        <div className="form-group">
          <label htmlFor="plan_description">
            Description <span className="required">*</span>
          </label>
          <textarea
            id="plan_description"
            name="plan_description"
            value={planFormData.plan_description}
            onChange={handlePlanInputChange}
            className={planErrors.plan_description ? "error" : ""}
            rows="3"
            placeholder="What's included in this plan?"
          />
          {renderFieldError("plan_description")}
        </div>

        <div className="form-row plan-form-row">
          <div className="form-group">
            <label htmlFor="plan_amount">
              Price (₹) <span className="required">*</span>
            </label>
            <input
              type="number"
              id="plan_amount"
              name="plan_amount"
              value={planFormData.plan_amount}
              onChange={handlePlanInputChange}
              className={planErrors.plan_amount ? "error" : ""}
              min="0"
              step="0.01"
              placeholder="0.00"
              inputMode="decimal"
            />
            {renderFieldError("plan_amount")}
          </div>

          <div className="form-group">
            <label htmlFor="plan_discount">
              Discount (₹) <span className="optional">Optional</span>
            </label>
            <input
              type="number"
              id="plan_discount"
              name="plan_discount"
              value={planFormData.plan_discount}
              onChange={handlePlanInputChange}
              className={planErrors.plan_discount ? "error" : ""}
              min="0"
              step="0.01"
              placeholder="0.00"
              inputMode="decimal"
            />
            {renderFieldError("plan_discount")}
          </div>

          <div className="form-group">
            <label htmlFor="plan_duration">
              Duration (months) <span className="required">*</span>
            </label>
            <input
              type="number"
              id="plan_duration"
              name="plan_duration"
              value={planFormData.plan_duration}
              onChange={handlePlanInputChange}
              className={planErrors.plan_duration ? "error" : ""}
              min="1"
              step="1"
              placeholder="e.g. 6"
              inputMode="numeric"
            />
            {renderFieldError("plan_duration")}
          </div>
        </div>
      </form>
    </Modal>
  );

  if (selectedCourse) {
    return (
      <div className="courses-page page">
        <div className="page-header">
          <div>
            <button type="button" className="back-link" onClick={closePlansView}>
              <i className="bi bi-arrow-left"></i> Courses
            </button>
            <h2>{selectedCourse.course_name}</h2>
            <p>Subscription plans students can buy for this course.</p>
          </div>
          <button type="button" className="btn-primary" onClick={() => openPlanModal()}>
            <i className="bi bi-plus-lg"></i> New plan
          </button>
        </div>

        {plansLoading ? (
          <LoadingState message="Loading plans…" />
        ) : plans.length === 0 ? (
          <EmptyState
            icon="bi bi-card-list"
            title="No plans yet"
            message="Create a plan so students can subscribe to this course."
            action={
              <button type="button" className="btn-primary" onClick={() => openPlanModal()}>
                <i className="bi bi-plus-lg"></i> Create first plan
              </button>
            }
          />
        ) : (
          <div className="plans-grid">
            {plans.map((plan) => {
              const discount = Number(plan.plan_discount) || 0;
              const duration = Number(plan.plan_duration) || 0;
              return (
                <article key={plan.id} className="plan-card card card-hover">
                  <div className="plan-card-head">
                    <div className="plan-card-title">
                      <h3>{plan.plan_name}</h3>
                      {duration > 0 && (
                        <span className="badge badge-brand">
                          <i className="bi bi-calendar3"></i> {duration} month{duration === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn-secondary btn-icon btn-sm"
                        onClick={() => openPlanModal(plan)}
                        title="Edit plan"
                        aria-label="Edit plan"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        type="button"
                        className="btn-danger btn-icon btn-sm"
                        onClick={() => handlePlanDelete(plan)}
                        title="Delete plan"
                        aria-label="Delete plan"
                      >
                        <i className="bi bi-trash3"></i>
                      </button>
                    </div>
                  </div>

                  <div className="plan-price">
                    <strong>{formatINR(plan.plan_amount)}</strong>
                    {discount > 0 && <span className="badge badge-success">{formatINR(discount)} off</span>}
                  </div>

                  <p className="plan-description">{plan.plan_description || "No description."}</p>
                </article>
              );
            })}
          </div>
        )}

        {planModal}
      </div>
    );
  }

  const query = search.trim().toLowerCase();
  const visibleCourses = courses.filter((c) => !query || (c.course_name || "").toLowerCase().includes(query));

  return (
    <div className="courses-page page">
      <div className="page-header">
        <div>
          <h2>Courses</h2>
          <p>Courses group your test series and carry their own subscription plans.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => openCourseModal()}>
          <i className="bi bi-plus-lg"></i> New course
        </button>
      </div>

      <ErrorAlert message={loadError} onRetry={loadCourses} />

      {loading ? (
        <LoadingState message="Loading courses…" />
      ) : courses.length === 0 ? (
        !loadError && (
          <EmptyState
            icon="bi bi-mortarboard"
            title="No courses yet"
            message="Create a course such as JEE or NEET, then add subscription plans to it."
            action={
              <button type="button" className="btn-primary" onClick={() => openCourseModal()}>
                <i className="bi bi-plus-lg"></i> Create first course
              </button>
            }
          />
        )
      ) : (
        <>
          {courses.length > 6 && (
            <div className="toolbar">
              <SearchField value={search} onChange={setSearch} placeholder="Search courses…" />
            </div>
          )}

          {visibleCourses.length === 0 ? (
            <EmptyState icon="bi bi-search" title="No matching courses" message="Try a different search term." />
          ) : (
            <div className="grid-cards courses-grid">
              {visibleCourses.map((course) => (
                <article key={course.id} className="course-card card card-hover">
                  <div className="course-card-head">
                    <span className="course-avatar">{(course.course_name || "?").charAt(0).toUpperCase()}</span>
                    <div className="course-card-title">
                      <h3 title={course.course_name}>{course.course_name}</h3>
                      <span className="text-muted">Course #{course.id}</span>
                    </div>
                  </div>

                  <div className="course-dates">
                    <div>
                      <span>Created</span>
                      <strong>{formatDate(course.created_at)}</strong>
                    </div>
                    <div>
                      <span>Last updated</span>
                      <strong>{formatDate(course.updated_at)}</strong>
                    </div>
                  </div>

                  <div className="course-actions">
                    <button type="button" className="btn-soft btn-sm" onClick={() => viewCoursePlans(course)}>
                      <i className="bi bi-card-list"></i> Plans
                    </button>
                    <button
                      type="button"
                      className="btn-secondary btn-icon btn-sm"
                      onClick={() => openCourseModal(course)}
                      title="Rename course"
                      aria-label="Rename course"
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button
                      type="button"
                      className="btn-danger btn-icon btn-sm"
                      onClick={() => handleCourseDelete(course)}
                      title="Delete course"
                      aria-label="Delete course"
                    >
                      <i className="bi bi-trash3"></i>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        open={isModalOpen}
        onClose={closeCourseModal}
        size="sm"
        icon={editingCourse ? "bi bi-pencil-square" : "bi bi-mortarboard"}
        title={editingCourse ? "Rename course" : "New course"}
        subtitle={editingCourse ? undefined : "You can add plans to it after creating it."}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeCourseModal} disabled={savingCourse}>
              Cancel
            </button>
            <button type="submit" form="courseForm" className="btn-primary" disabled={savingCourse}>
              {savingCourse ? <span className="btn-spinner"></span> : <i className="bi bi-check2"></i>}
              {editingCourse ? "Save" : "Create course"}
            </button>
          </>
        }
      >
        <form id="courseForm" onSubmit={handleCourseSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="course_name">
              Course name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="course_name"
              name="course_name"
              value={courseName}
              onChange={(e) => {
                setCourseName(e.target.value);
                if (courseError) setCourseError("");
              }}
              className={courseError ? "error" : ""}
              maxLength="255"
              placeholder="e.g. JEE Mains"
              autoFocus
            />
            {courseError && (
              <span className="field-error">
                <i className="bi bi-exclamation-circle"></i> {courseError}
              </span>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default Courses;
